/**
 * Phase 133 — StreamX Polish Sweep
 * Inline upgrade nudge banner. Shown on /livex/dashboard when an academy
 * is on Pilot and either approaching the monthly cap (>=80%) or has hit it.
 * Encourages upgrade with a celebratory, non-billing tone (growth, not nag).
 */
import { Link } from 'react-router-dom';
import { Crown, Sparkles, AlertTriangle, ArrowRight } from 'lucide-react';

export default function LivexUpgradeNudge({ usage, remaining, percent }) {
  const blocked = remaining === 0;
  const ringHue = blocked ? '#dc2626' : '#FF5A1F';

  return (
    <div
      data-testid="livex-dashboard-upgrade-nudge"
      className="relative p-5 sm:p-6 rounded-2xl border-2 overflow-hidden"
      style={{
        background: blocked
          ? 'linear-gradient(135deg, #2a0a0e, #07111F)'
          : 'linear-gradient(135deg, #1a0e10, #07111F)',
        borderColor: ringHue,
        boxShadow: `0 10px 32px ${ringHue}22`,
      }}
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(50% 60% at 100% 50%, ${ringHue}30, transparent 60%)`,
        }}
      />
      <div className="relative flex items-start gap-4 flex-wrap">
        <div
          className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl"
          style={{ background: `linear-gradient(135deg, #dc2626, #f97316)`, boxShadow: `0 8px 20px ${ringHue}55` }}
        >
          {blocked ? <AlertTriangle className="h-6 w-6 text-white" /> : <Crown className="h-6 w-6 text-white" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black uppercase tracking-[0.2em] mb-1" style={{ color: blocked ? '#fda4af' : '#fed7aa' }}>
            {blocked ? 'Monthly limit reached' : 'You\'re crushing it'}
          </div>
          <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">
            {blocked
              ? `You've used all ${usage} pilot streams this month`
              : `${usage} of your free streams used (${percent}%)`}
          </h3>
          <p className="livex-body-text mt-1" style={{ fontSize: '13px' }}>
            {blocked
              ? 'Upgrade to StreamX Pro to keep broadcasting matches without limits, drop the watermark, and unlock priority support.'
              : 'Going strong! Upgrade before you hit the cap — unlimited streams, no viewer cap, and zero "Powered by StreamX" branding.'}
          </p>
        </div>
        <Link
          to="/streamx/subscription"
          data-testid="livex-dashboard-upgrade-nudge-cta"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', boxShadow: '0 8px 24px rgba(255,90,31,0.30)' }}
        >
          <Sparkles className="h-4 w-4" /> Upgrade to Pro <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
